import { defineStore } from 'pinia';
import { ref, computed, reactive, watchEffect, watch } from 'vue';
import { useMerchantStore } from '~/store/merchant';
import type { TemplateOverride } from '@monolog/shared/types/database/communications-svc';
import type { BrandingSetting } from '@monolog/shared/types/database/merchant-svc';
import type { Widget, InfoModalFormValues, WidgetFormValues, WidgetConfigElement } from '~/types/widget';
import type { ThemeBranding } from '@monolog/shared/types';

// Email utilities
import { generateEmailTemplate, renderEmailFromBlocks, type EmailTemplateData } from '../utils/email-templates';
import {
  createInitialFormValues,
  getAvailableTemplates,
  setAdvancedConfigFromContent,
  STORE_LEVEL_EMAIL_KEYS,
} from '../utils/email-form-helpers';
import { ADMIN_SECTION_LABELS } from '@/constants/emails/emails.constants';

// Visual email builder utilities
import {
  parseBlocks,
  stripIds as stripEmailIds,
  findBlock,
  setBlockAttr,
  removeBlock,
  insertBlock,
  moveBlock as moveBlockInList,
  duplicateBlock,
  buildBlockFromSchema,
  blockTypeExists,
  isBlockList,
  type EmailBlock,
} from '../email-builder/useEmailBlockTree';
import {
  seedBlocksFromFormValues,
  buildRenderContext,
  buildDocumentVars,
  mapBlockAttrToFormValue,
} from '../email-builder/useEmailSeed';
import { getBlockSchema, type EmailBlockSchema } from '../email-builder/catalog';

// Widget utilities
import { createInitialWidgetFormValues } from '../utils/cart-widget-helpers';

// Visual builder utilities
import {
  parseConfigTree,
  stripIds,
  setNodeAttr,
  insertNode,
  removeNode,
  moveNode,
  duplicateNode,
  buildNodeFromCatalog,
  normalizeContainers,
  findNode,
  type BuilderNode,
} from '../builder/useBuilderTree';
import { convertNode } from '../builder/useBuilderConvert';
import { ensureCoverageTiles } from '../builder/coverage-tiles';
import type { CatalogMap, ElementSchema } from '../builder/catalog';
import { createInitialInfoModalFormValues } from '../utils/info-modal-helpers';
import cloneDeep from 'lodash/cloneDeep';

/** Host serving the per-brand widget bundles. Each stream adds its own prefix beneath it. */
const WIDGET_CDN_BASE = 'https://cdn.orderprotection.com';

/**
 * `WIDGET_CDN_BASE` lets local dev serve a bundle of its own, because brands are not deployed to
 * the CDN there and the preview has nothing to render without one. Deployed envs never set it.
 */
const widgetCdnBase = (): string => {
  // A Nuxt auto-import, so it is absent under unit tests, where the default host is the point.
  const configured = typeof useRuntimeConfig === 'function' ? useRuntimeConfig().public?.widgetCdnBase : undefined;

  return typeof configured === 'string' && configured ? configured : WIDGET_CDN_BASE;
};

// Types
interface EmailFormValues {
  [key: string]: any; // Will be properly typed from email-form-helpers
}

// Email compilation helper
function createEmailTemplateData(
  formValues: EmailFormValues,
  selectedTemplateKey: string,
  isPreview: boolean,
): EmailTemplateData {
  return {
    buttonBackground: formValues.buttonBackground,
    buttonBorderRadius: formValues.buttonBorderRadius,
    buttonFontColor: formValues.buttonFontColor,
    buttonFontStyle: formValues.buttonFontStyle,
    buttonLink: formValues.buttonLink,
    buttonText: formValues.buttonText,
    customDomain: 'orderprotection.com',
    desktopLogoWidth: formValues.desktopLogoWidth,
    footerContent: formValues.footerContent,
    headline: formValues.headline,
    headlineDesktopFontColor: formValues.headlineDesktopFontColor,
    headlineDesktopFontFamily: formValues.headlineDesktopFontFamily,
    headlineDesktopFontSize: formValues.headlineDesktopFontSize,
    headlineDesktopFontWeight: formValues.headlineDesktopFontWeight,
    headlineDesktopLetterSpacing: formValues.headlineDesktopLetterSpacing,
    headlineDesktopLineHeight: formValues.headlineDesktopLineHeight,
    headlineFontStyle: formValues.headlineFontStyle,
    headlineMobileFontColor: formValues.headlineMobileFontColor,
    headlineMobileFontFamily: formValues.headlineMobileFontFamily,
    headlineMobileFontSize: formValues.headlineMobileFontSize,
    headlineMobileFontWeight: formValues.headlineMobileFontWeight,
    iconUrl: formValues.iconUrl,
    isPreview,
    logoUrl: formValues.logoUrl,
    mobileLogoWidth: formValues.mobileLogoWidth,
    paragraph: formValues.paragraph,
    paragraphDesktopFontColor: formValues.paragraphDesktopFontColor,
    paragraphDesktopFontFamily: formValues.paragraphDesktopFontFamily,
    paragraphDesktopFontSize: formValues.paragraphDesktopFontSize,
    paragraphDesktopFontWeight: formValues.paragraphDesktopFontWeight,
    paragraphDesktopLetterSpacing: formValues.paragraphDesktopLetterSpacing,
    paragraphDesktopLineHeight: formValues.paragraphDesktopLineHeight,
    paragraphFontStyle: formValues.paragraphFontStyle,
    paragraphMobileFontColor: formValues.paragraphMobileFontColor,
    paragraphMobileFontFamily: formValues.paragraphMobileFontFamily,
    paragraphMobileFontSize: formValues.paragraphMobileFontSize,
    paragraphMobileFontWeight: formValues.paragraphMobileFontWeight,
    selectedTemplateKey,
    storeName: formValues.storeName,
    subText: formValues.subText,
    subTextDesktopFontColor: formValues.subTextDesktopFontColor,
    subTextDesktopFontFamily: formValues.subTextDesktopFontFamily,
    subTextDesktopFontSize: formValues.subTextDesktopFontSize,
    subTextDesktopFontStyle: formValues.subTextDesktopFontStyle,
    subTextDesktopFontWeight: formValues.subTextDesktopFontWeight,
    subTextDesktopLetterSpacing: formValues.subTextDesktopLetterSpacing,
    subTextDesktopLineHeight: formValues.subTextDesktopLineHeight,
    togglePoweredByOpEnabled: formValues.togglePoweredByOpEnabled,
  };
}

export const useCustomizationsStore = defineStore('customizations', () => {
  const merchantStore = useMerchantStore();

  // ===== COMPUTED PROPERTIES =====
  const storeName = computed(() => merchantStore.currentStore?.name || 'BrandName');

  const showStoreCredit = computed(() => merchantStore.currentStore?.generalSettings?.offerStoreCreditOnOpPurchase);

  const availableTemplates = computed(() => getAvailableTemplates(merchantStore));

  const selectedTemplate = computed(() => availableTemplates.value[selectedTemplateKey.value]);

  const currentTemplateOverrideComputed = computed(() => {
    if (selectedTemplate.value) {
      return templateOverrides.value[selectedTemplate.value.templateName] || null;
    }

    return null;
  });

  // ===== STATE =====
  // Branding & White Label
  const whiteLabelSettings = reactive<Partial<BrandingSetting>>({});
  const customDomain = reactive({ customDomain: '' });

  // Email State
  const selectedTemplateKey = ref('approved');
  const currentTemplateOverride = ref<TemplateOverride | null>(null);
  const templateOverrides = ref<Record<string, TemplateOverride>>({});

  // User-editable form values (reactive ref) - directly editable
  const formValues = ref<EmailFormValues>({});

  // ===== Visual Email Builder State =====
  // The flat block stack edited by the drag-and-drop builder. Blocks carry editor-only ids; the
  // stripped list is mirrored into formValues.emailBlocks (so dirty-tracking + save round-trip work)
  // and compiled to HTML via renderEmailFromBlocks when isEmailBuilderActive is true.
  const emailBlocks = ref<EmailBlock[]>([]);
  const emailSelectedBlockId = ref<string | null>(null);
  const isEmailBuilderActive = ref(false);

  // ===== Store-level email settings (logo + footer) =====
  // Logo + footer are per-store, not per-template. `sharedEmailSettings` holds the live value;
  // editing it on any template propagates everywhere (deep-watch below) and persists to all overrides
  // on save. Gated by `storeLevelEmailReady` so the watch ignores the initial default-template build.
  const sharedEmailSettings = ref<Record<string, unknown>>({});
  const storeLevelEmailReady = ref(false);

  // Computed form values for the selected template - uses cached data when available
  const templateFormValues = computed(() => {
    const template = availableTemplates.value[selectedTemplateKey.value];
    if (!template) return defaultFormValues.value;

    // Check if we have cached customization values for this template
    const cachedOverride = templateOverrides.value[template.templateName];

    if (cachedOverride) {
      // Use cached customization values
      const formValues = createInitialFormValues(
        cachedOverride.customizationValues,
        selectedTemplateKey.value,
        availableTemplates.value,
        storeName.value,
      );
      setAdvancedConfigFromContent(formValues, cachedOverride);

      return formValues;
    } else {
      // No cached data, use template defaults
      return createInitialFormValues(undefined, selectedTemplateKey.value, availableTemplates.value, storeName.value);
    }
  });

  const defaultFormValues = computed(() =>
    createInitialFormValues(undefined, selectedTemplateKey.value, availableTemplates.value, storeName.value),
  );

  const preservedAdvancedConfig = ref<string>('');

  watch(
    templateFormValues,
    newTemplateValues => {
      if (Object.keys(newTemplateValues).length > 0) {
        formValues.value = { ...newTemplateValues };
        if (preservedAdvancedConfig.value) {
          formValues.value.advancedConfig = preservedAdvancedConfig.value;
        }
      }
    },
    { immediate: true },
  );

  watch(
    () => formValues.value?.advancedConfig,
    newValue => {
      if (newValue !== undefined && newValue !== preservedAdvancedConfig.value) {
        preservedAdvancedConfig.value = newValue;
      }
    },
  );

  // Reset email state when the active store changes
  watch(
    () => merchantStore.currentStore?.id,
    (newId, oldId) => {
      if (newId && oldId && newId !== oldId) {
        templateOverrides.value = {};
        currentTemplateOverride.value = null;
        formValues.value = {};
        preservedAdvancedConfig.value = '';
        selectedTemplateKey.value = 'approved';
      }
    },
  );

  // Widget State
  const widgets = ref<Widget[]>([]);
  const selectedWidget = ref<Widget | null>(null);
  const widgetFormValues = ref<WidgetFormValues>(createInitialWidgetFormValues());

  // Info Modal State
  const infoModals = ref<Widget[]>([]);
  const selectedInfoModal = ref<Widget | null>(null);
  const infoModalFormValues = ref<InfoModalFormValues>(createInitialInfoModalFormValues());

  // ===== Visual Builder State =====
  // The cart widget config tree edited directly by the drag-and-drop visual builder.
  // Nodes carry editor-only ids; call getBuilderConfig() to get clean config for save/preview.
  const builderConfigTree = ref<BuilderNode[]>([]);
  const builderSelectedNodeId = ref<string | null>(null);
  const isVisualBuilderActive = ref(false);
  // Monotonic counter the panel watches to scroll its Properties section into view —
  // bumped only when a selection originates from a preview click (not from a layer click,
  // since the user is already in the sidebar in that case).
  const builderPropertiesScrollTick = ref(0);

  // Theme Branding State
  const themeBranding = ref<ThemeBranding | null>(null);

  function setThemeBranding(branding: ThemeBranding) {
    themeBranding.value = branding;
  }

  function clearThemeBranding() {
    themeBranding.value = null;
  }

  // Brand name normalization for script source url
  /** The CDN slug convention shared with dispatch's brand-deploy pipeline. */
  const slugify = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  // No fallback slug on purpose. Every slug on the CDN is a DIFFERENT brand's deploy, so a
  // stand-in does not degrade to "generic" — it silently serves another merchant's widget, at
  // whatever version that brand last shipped. An empty slug is the honest answer.
  const normalizedBrandName = computed(() => slugify(merchantStore.currentStore?.name || ''));

  // The subscriptions stream is deployed under the store's myshopify SUBDOMAIN, not its display
  // name (dispatch-svc implementation.service.ts, "deploy subscriptions widget under the myshopify
  // subdomain"). Building it from the name handed out a URL that only worked for brands whose name
  // happened to match their subdomain.
  const normalizedSubscriptionSlug = computed(() =>
    slugify((merchantStore.currentStore?.storeDomain || '').replace(/\.myshopify\.com$/i, '')),
  );

  /**
   * A widget bundle URL for the given slug, or '' when there is none to build one from. Both
   * streams resolve through here so a guard added to one cannot be missed on the other — the
   * subscription URL kept its own copy of this and emitted `subscriptions-widget//widget.js`.
   */
  const bundleUrl = (cdnPrefix: string, slug: string) =>
    slug ? `${widgetCdnBase()}/${cdnPrefix}/${slug}/widget.js` : '';

  /**
   * Empty in, empty out: a `<script src="">` loads nothing and explains nothing, so a snippet built
   * around a missing slug is worse to hand a merchant than no snippet at all.
   */
  const scriptTagFor = (url: string) => (url ? `<script src="${url}"></script>` : '');

  const scriptSourceUrl = computed(() => bundleUrl('cart-widget', normalizedBrandName.value));
  const scriptTag = computed(() => scriptTagFor(scriptSourceUrl.value));

  // Subscription widget ships from a separate CDN prefix so it can coexist with the
  // brand's cart widget without colliding on widget.js.
  const subscriptionScriptSourceUrl = computed(() =>
    bundleUrl('subscriptions-widget', normalizedSubscriptionSlug.value),
  );
  const subscriptionScriptTag = computed(() => scriptTagFor(subscriptionScriptSourceUrl.value));

  // ===== EMAIL COMPILATION =====
  // Precedence: advancedConfig (raw HTML override) > visual builder blocks > legacy flat-form compile.
  // advancedConfig and the builder are mutually exclusive (the builder tab is hidden when advancedConfig is set).
  function resolveCompiledHtml(isPreview: boolean): string {
    const advancedConfig = formValues.value?.advancedConfig;
    if (advancedConfig && typeof advancedConfig === 'string' && advancedConfig.trim().length > 0) {
      return advancedConfig;
    }

    if (isEmailBuilderActive.value && emailBlocks.value.length > 0) {
      const ctx = buildRenderContext(formValues.value, storeName.value, isPreview);
      const vars = buildDocumentVars(formValues.value);

      return renderEmailFromBlocks(stripEmailIds(emailBlocks.value), ctx, vars);
    }

    const templateData = createEmailTemplateData(formValues.value, selectedTemplateKey.value, isPreview);

    return generateEmailTemplate(templateData);
  }

  const compiledHtml = computed(() => resolveCompiledHtml(true));

  function getCompiledHtml(isPreview: boolean = false): string {
    return resolveCompiledHtml(isPreview);
  }

  // ===== METHODS =====
  // Branding & White Label Methods
  function setWhiteLabelSettings(settings: Partial<BrandingSetting>) {
    Object.assign(whiteLabelSettings, settings);
  }

  function setCustomDomain(domain: string) {
    customDomain.customDomain = domain;
  }

  // Email Methods
  function setSelectedTemplate(templateKey: string) {
    if (templateKey in availableTemplates.value) {
      selectedTemplateKey.value = templateKey;

      // Update current template override
      const template = availableTemplates.value[templateKey];
      if (template) {
        currentTemplateOverride.value = templateOverrides.value[template.templateName] || null;
      }
    }
  }

  // ===== Visual Email Builder Methods =====
  // Mirror the live block list into formValues so dirty-tracking fires and the save payload
  // (customizations: formValues) round-trips the blocks. __builderMode tells the next load to
  // reopen in the builder. Not tied to a reactive watch — called explicitly after each mutation.
  function syncBuilderToFormValues() {
    formValues.value.emailBlocks = stripEmailIds(emailBlocks.value);
    // Only mark as visual when actively in the builder — never write 'flat' here, because tab
    // navigation (setEmailBuilderActive(false)) must not override the saved builder-mode marker and
    // flip a visually-built email back to flat on the next load. 'flat' is implied by absence of 'visual'.
    if (isEmailBuilderActive.value) {
      formValues.value.__builderMode = 'visual';
    }
  }

  // Returns the clean (id-stripped) block list, e.g. for compiling.
  function getBlocksConfig() {
    return stripEmailIds(emailBlocks.value);
  }

  // Seeds the block stack from the current template + formValues (legacy block order).
  function seedEmailBlocks() {
    emailBlocks.value = parseBlocks(seedBlocksFromFormValues(formValues.value, selectedTemplateKey.value));
    emailSelectedBlockId.value = null;
    syncBuilderToFormValues();
  }

  // Restores builder state from formValues after a load / template switch (called by the view-model).
  // Only ever ACTIVATES (to reopen a saved visual build); never force-deactivates, so the active flag
  // stays owned by the sidebar tab. If the user is already in the builder and switches to a template
  // with no saved blocks, seed a default stack so the builder stays usable across the switch.
  function syncBuilderFromFormValues() {
    const persisted = formValues.value?.emailBlocks;
    const hasPersisted = isBlockList(persisted) && persisted.length > 0;
    emailSelectedBlockId.value = null;

    if (formValues.value?.__builderMode === 'visual' && hasPersisted) {
      emailBlocks.value = parseBlocks(persisted);
      isEmailBuilderActive.value = true;

      return;
    }

    emailBlocks.value = hasPersisted ? parseBlocks(persisted) : [];

    if (isEmailBuilderActive.value && emailBlocks.value.length === 0) {
      emailBlocks.value = parseBlocks(seedBlocksFromFormValues(formValues.value, selectedTemplateKey.value));
      syncBuilderToFormValues();
    }
  }

  // Re-activating the builder reuses any in-memory blocks rather than re-seeding. Those blocks only
  // ever belong to the current template/session: a template switch runs switchToTemplate, which rebuilds
  // formValues and calls syncBuilderFromFormValues (resetting emailBlocks), so blocks can't leak across
  // templates. Within one template they're intentionally preserved (no re-seed on tab toggle).
  function setEmailBuilderActive(active: boolean) {
    isEmailBuilderActive.value = active;
    if (active && emailBlocks.value.length === 0) {
      seedEmailBlocks();

      return;
    }
    syncBuilderToFormValues();
  }

  // v-model setter for the preview sortable (reorders the whole list).
  function setEmailBlocks(blocks: EmailBlock[]) {
    emailBlocks.value = blocks;
    syncBuilderToFormValues();
  }

  function selectEmailBlock(id: string | null) {
    emailSelectedBlockId.value = id;
  }

  function updateEmailBlockAttr(id: string, attr: string, value: string | number | boolean) {
    const block = findBlock(emailBlocks.value, id);
    if (!block) return;

    setBlockAttr(emailBlocks.value, id, attr, value);

    // Keep the matching flat formValues key in sync (legacy compile fallback + "save as default" flow).
    const fvKey = mapBlockAttrToFormValue(block.type, attr);
    if (fvKey) formValues.value[fvKey] = value;

    syncBuilderToFormValues();
  }

  function addEmailBlock(schema: EmailBlockSchema, index?: number) {
    // Enforce singletons (header/footer/icon): no-op if one already exists anywhere in the tree.
    if (schema.singleton && blockTypeExists(emailBlocks.value, schema.type)) return null;

    const block = buildBlockFromSchema(schema);
    const selectedIndex = emailSelectedBlockId.value
      ? emailBlocks.value.findIndex(b => b.__builderId === emailSelectedBlockId.value)
      : -1;
    const targetIndex = index ?? (selectedIndex >= 0 ? selectedIndex + 1 : emailBlocks.value.length);
    insertBlock(emailBlocks.value, targetIndex, block);
    emailSelectedBlockId.value = block.__builderId;
    syncBuilderToFormValues();

    return block;
  }

  function removeEmailBlock(id: string) {
    const removed = removeBlock(emailBlocks.value, id);
    if (removed && emailSelectedBlockId.value === id) emailSelectedBlockId.value = null;
    syncBuilderToFormValues();

    return removed;
  }

  function moveEmailBlock(id: string, index: number) {
    const moved = moveBlockInList(emailBlocks.value, id, index);
    syncBuilderToFormValues();

    return moved;
  }

  function duplicateEmailBlock(id: string) {
    // Don't duplicate singletons (header/footer/icon) — the rest of the system assumes one exists.
    const source = findBlock(emailBlocks.value, id);
    if (source && getBlockSchema(source.type)?.singleton) return null;

    const clone = duplicateBlock(emailBlocks.value, id);
    if (clone) emailSelectedBlockId.value = clone.__builderId;
    syncBuilderToFormValues();

    return clone;
  }

  // ===== Store-level email settings (logo + footer) =====
  function captureSharedEmailSettings() {
    for (const key of STORE_LEVEL_EMAIL_KEYS) {
      if (formValues.value[key] !== undefined) sharedEmailSettings.value[key] = formValues.value[key];
    }
  }

  // Overlay the shared logo/footer onto the freshly-loaded formValues so they "stick" across templates.
  function applySharedEmailToFormValues() {
    for (const key of STORE_LEVEL_EMAIL_KEYS) {
      if (sharedEmailSettings.value[key] !== undefined) formValues.value[key] = sharedEmailSettings.value[key];
    }
  }

  // Mirror the shared logo/footer onto the matching builder block attributes (logo/footer blocks),
  // so the visual builder shows the store-level values regardless of what a template persisted.
  function applySharedEmailToBlocks() {
    if (!emailBlocks.value.length) return;

    const visit = (block: EmailBlock) => {
      for (const attr of Object.keys(block.attributes)) {
        const fvKey = mapBlockAttrToFormValue(block.type, attr);
        if (
          fvKey &&
          (STORE_LEVEL_EMAIL_KEYS as readonly string[]).includes(fvKey) &&
          formValues.value[fvKey] !== undefined
        ) {
          block.attributes = { ...block.attributes, [attr]: formValues.value[fvKey] as string | number | boolean };
        }
      }
      block.children?.forEach(visit);
    };
    emailBlocks.value.forEach(visit);

    if (isEmailBuilderActive.value) syncBuilderToFormValues();
  }

  // Seed the shared settings from the (real) current template after initial load, then arm the watch.
  function initStoreLevelEmailSettings() {
    captureSharedEmailSettings();
    storeLevelEmailReady.value = true;
  }

  // Capture-on-edit / apply-on-switch. Mutations keep the same object identity (nv === ov) → an edit,
  // so we capture; a replacement (template switch / load) gives a new object → apply the shared values.
  watch(
    formValues,
    (nv, ov) => {
      if (!storeLevelEmailReady.value) return;
      if (nv === ov) {
        captureSharedEmailSettings();
      } else {
        applySharedEmailToFormValues();
        applySharedEmailToBlocks();
      }
    },
    { deep: true },
  );

  // Widget Methods
  function setWidgets(newWidgets: Widget[]) {
    widgets.value = newWidgets;
  }

  function addWidget(widget: Widget) {
    widgets.value.push(widget);
  }

  function setSelectedWidget(widget: Widget | null) {
    selectedWidget.value = widget;

    if (widget) {
      const widgetCustomizations = widget.customizationValues || (widget as any).customizations || {};
      const newWidgetFormValues = createInitialWidgetFormValues(
        widgetCustomizations,
        widget.type || 'CART',
        widget.variant || 'toggle',
      );
      Object.assign(widgetFormValues.value, newWidgetFormValues);
    }
  }

  function clearSelectedWidget() {
    selectedWidget.value = null;
  }

  // Visual Builder Methods
  function setBuilderTree(config: unknown, containerTypes: string[] = []) {
    builderConfigTree.value = ensureCoverageTiles(parseConfigTree(config));
    builderSelectedNodeId.value = null;
    if (containerTypes.length) normalizeContainers(builderConfigTree.value, containerTypes);
  }

  // Ensures container nodes already in the tree have a children array (drag-and-drop drop targets).
  function normalizeBuilderTree(containerTypes: string[]) {
    normalizeContainers(builderConfigTree.value, containerTypes);
  }

  // Replaces the root list (used as the drag-and-drop v-model setter for the top level).
  function setBuilderRoot(nodes: BuilderNode[]) {
    builderConfigTree.value = nodes;
  }

  // Replaces a node's children (used as the drag-and-drop v-model setter for a nested list).
  function setBuilderNodeChildren(id: string, children: BuilderNode[]) {
    const node = findNode(builderConfigTree.value, id);
    if (node) node.children = children;
  }

  function getBuilderConfig(): WidgetConfigElement[] {
    return stripIds(builderConfigTree.value);
  }

  function selectBuilderNode(id: string | null) {
    builderSelectedNodeId.value = id;
  }

  // Selecting a node from a preview click — also signals the panel to scroll its Properties
  // section into view (the user is interacting with the preview, not the layer list).
  function selectBuilderNodeFromPreview(id: string | null) {
    builderSelectedNodeId.value = id;
    builderPropertiesScrollTick.value += 1;
  }

  function updateBuilderNodeAttr(id: string, attr: string, value: string | number | boolean) {
    setNodeAttr(builderConfigTree.value, id, attr, value);
  }

  // Turns an element into a compatible type in place — a decline link into a filled button, say —
  // keeping its wording, styling and `emits` (which is what preserves the third-party selectors).
  function convertBuilderNode(id: string, targetType: string, catalog: CatalogMap) {
    return !!convertNode(builderConfigTree.value, id, targetType, catalog);
  }

  function addBuilderElement(schema: ElementSchema, parentId: string | null = null, index?: number) {
    const node = buildNodeFromCatalog(schema);
    const targetIndex = index ?? (parentId ? 0 : builderConfigTree.value.length);
    const inserted = insertNode(builderConfigTree.value, parentId, targetIndex, node);
    if (inserted) builderSelectedNodeId.value = node.__builderId;

    return inserted ? node : null;
  }

  function insertBuilderNode(parentId: string | null, index: number, node: BuilderNode) {
    return insertNode(builderConfigTree.value, parentId, index, node);
  }

  function removeBuilderNode(id: string) {
    const removed = removeNode(builderConfigTree.value, id);
    if (removed && builderSelectedNodeId.value === id) builderSelectedNodeId.value = null;

    return removed;
  }

  function moveBuilderNode(id: string, targetParentId: string | null, index: number) {
    return moveNode(builderConfigTree.value, id, targetParentId, index);
  }

  function duplicateBuilderNode(id: string) {
    const clone = duplicateNode(builderConfigTree.value, id);
    if (clone) builderSelectedNodeId.value = clone.__builderId;

    return clone;
  }

  function setVisualBuilderActive(active: boolean) {
    isVisualBuilderActive.value = active;
  }

  // Info Modal Methods
  function setInfoModals(newInfoModals: Widget[]) {
    infoModals.value = newInfoModals;
  }

  function setSelectedInfoModal(infoModal: Widget | null) {
    selectedInfoModal.value = infoModal;

    if (infoModal) {
      const customizations = (infoModal.customizationValues || {}) as Record<string, unknown>;
      const newFormValues = createInitialInfoModalFormValues(customizations);
      Object.assign(infoModalFormValues.value, cloneDeep(newFormValues));
    }
  }

  function clearSelectedInfoModal() {
    selectedInfoModal.value = null;
  }

  return {
    // Constants
    ADMIN_SECTION_LABELS,

    addBuilderElement,

    // Methods
    addEmailBlock,

    addWidget,

    // Computed Properties
    availableTemplates,

    // Visual Builder State + Methods
    builderConfigTree,

    builderPropertiesScrollTick,

    builderSelectedNodeId,

    clearSelectedInfoModal,

    clearSelectedWidget,

    clearThemeBranding,

    compiledHtml,

    convertBuilderNode,

    currentTemplateOverride,

    currentTemplateOverrideComputed,

    // State
    customDomain,

    duplicateBuilderNode,

    duplicateEmailBlock,

    emailBlocks,

    emailSelectedBlockId,

    formValues,

    getBlocksConfig,

    getBuilderConfig,

    getCompiledHtml,

    infoModalFormValues,

    infoModals,

    initStoreLevelEmailSettings,

    insertBuilderNode,

    isEmailBuilderActive,

    isVisualBuilderActive,

    moveBuilderNode,

    moveEmailBlock,

    normalizeBuilderTree,

    // Brand & Script Computed Properties
    normalizedBrandName,

    removeBuilderNode,

    removeEmailBlock,

    scriptSourceUrl,
    scriptTag,
    seedEmailBlocks,
    selectBuilderNode,

    selectBuilderNodeFromPreview,

    selectEmailBlock,

    selectedInfoModal,

    selectedTemplate,

    selectedTemplateKey,

    selectedWidget,
    setBuilderNodeChildren,
    setBuilderRoot,
    setBuilderTree,

    setCustomDomain,

    setEmailBlocks,

    setEmailBuilderActive,

    setInfoModals,

    setSelectedInfoModal,

    setSelectedTemplate,

    setSelectedWidget,

    setThemeBranding,

    setVisualBuilderActive,
    setWhiteLabelSettings,
    setWidgets,
    showStoreCredit,
    storeName,
    subscriptionScriptSourceUrl,
    subscriptionScriptTag,

    syncBuilderFromFormValues,

    templateOverrides,
    themeBranding,

    updateBuilderNodeAttr,

    updateEmailBlockAttr,

    whiteLabelSettings,
    widgetFormValues,
    widgets,
  };
});
